// src/pages/factors/Edit.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../core/api';
import './Factors.css';
import FactorFormSkeleton from './FactorFormSkeleton';
import FactorItemsTable from './FactorItemsTable';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import { parseNum, emptyItem, getTodayJalali, calcTotals, formatNumber } from '../../utils/factorHelpers';
import CustomerPicker from './CustomerPicker';

const Edit = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const [serial, setSerial] = useState('');
  const [date, setDate] = useState('');

  const [items, setItems] = useState([]);
  const [paymentType, setPaymentType] = useState('نقدی');
  const [notes, setNotes] = useState('');

  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');

  const [isDirty, setIsDirty] = useState(false);
  const isDirtyRef = useRef(false);

  const [customer, setCustomer] = useState(null);

  const [prepaid, setPrepaid] = useState('');

  const grandTotal = calcTotals(
    items.map((it) => ({
      qty: parseNum(it.qty),
      price: parseNum(it.price),
      discount: parseNum(it.discount),
      tax: it.hasTax
        ? Math.round(
            (parseNum(it.qty) * parseNum(it.price) - parseNum(it.discount)) * 0.1
          )
        : 0,
    }))
  ).total;

  const prepaidNum = parseNum(prepaid);
  const debitAmount =
    paymentType === 'غیر نقدی'
      ? grandTotal
      : paymentType === 'پیش پرداخت'
      ? grandTotal - prepaidNum
      : 0;
  /* ============== Fetch ( factor) ============== */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const factorRes = await api.get(`/factors/${id}`);
        const factor = factorRes.data?.factor || factorRes.data;

        if (!factor || !factor.id) {
          if (!cancelled) setNotFound(true);
          return;
        }

        if (cancelled) return;

        setSerial(factor.serial || '');
        setDate(factor.date || '');
        setCustomer(
          factor.customerId ? { id: factor.customerId, ...factor.buyer } : null
        );

        setPaymentType(factor.paymentType || 'نقدی');
        setPrepaid(factor.prepaid ? String(Math.round(Number(factor.prepaid))) : '');
        setNotes(factor.notes || '');

        const loadedItems = (factor.items || []).map((it) => ({
          ...emptyItem(),
          code: it.code || '',
          name: it.name || '',
          unit: it.unit || '',
            description: it.description || '',   // ← این خط

          qty: Number(it.qty) || 1,
          price: Number(it.price) || 0,
          discount: Number(it.discount) || 0,
          hasTax: Number(it.tax) > 0,
        }));
        setItems(loadedItems.length > 0 ? loadedItems : [emptyItem()]);
      } catch (err) {
        if (cancelled) return;
        console.error('خطا در دریافت اطلاعات:', err);
        if (err.response?.status === 404) {
          setNotFound(true);
        } else {
          setMessage('خطا در دریافت اطلاعات فاکتور');
          setMessageType('danger');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  /* ============== Toast auto-hide ============== */
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(''), 3000);
    return () => clearTimeout(t);
  }, [message]);

  /* ============== beforeunload ============== */
  useEffect(() => {
    const handler = (e) => {
      if (!isDirtyRef.current) return;
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  /* ============== Handlers ============== */
  const markDirty = () => {
    if (!isDirtyRef.current) {
      isDirtyRef.current = true;
      setIsDirty(true);
    }
  };

  const handleSelectCustomer = (c) => {
    setCustomer(c);
    markDirty();
  };

  /* ============== Submit ============== */
  const handleSubmit = async (e) => {
    e.preventDefault();

    let finalDate = date;
    if (!finalDate) {
      finalDate = getTodayJalali();
      setDate(finalDate);
    }

    if (!customer?.id) {
      setMessage('لطفاً یک مشتری انتخاب کنید');
      setMessageType('danger');
      return;
    }
    if (!date) {
      setMessage('تاریخ فاکتور الزامی است');
      setMessageType('danger');
      return;
    }

    if (items.length === 0 || !items[0].name?.trim()) {
      setMessage('حداقل یک قلم با نام کالا وارد کنید');
      setMessageType('danger');
      return;
    }

    if (paymentType === 'پیش پرداخت') {
      if (prepaidNum <= 0) {
        setMessage('مبلغ پیش پرداخت را وارد کنید');
        setMessageType('danger');
        return;
      }
      if (prepaidNum > grandTotal) {
        setMessage('مبلغ پیش پرداخت نمی‌تواند بیشتر از مبلغ کل باشد');
        setMessageType('danger');
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        type: 1,
        date: finalDate,
        customerId: customer.id,

        items: items.map((it) => ({
          code: it.code || '',
          name: it.name || '',
         unit: it.unit || '',
description: it.description || '',
          qty: parseNum(it.qty) || 1,
          price: parseNum(it.price) || 0,
          discount: parseNum(it.discount) || 0,
          tax: it.hasTax
            ? Math.round(
                (parseNum(it.qty) * parseNum(it.price) -
                  parseNum(it.discount)) *
                  0.1
              )
            : 0,
        })),
        paymentType,
        prepaid: paymentType === 'پیش پرداخت' ? prepaidNum : 0,
        notes,
      };

      await api.post(`/factors/${id}/edit`, payload);

      setIsDirty(false);
      isDirtyRef.current = false;
      setMessage('تغییرات با موفقیت ذخیره شد');
      setMessageType('success');

      setTimeout(() => {
        navigate('/dashboard/factors');
      }, 1000);
    } catch (err) {
      console.error('خطا در ذخیره تغییرات:', err);
      setMessage(err.response?.data?.error || 'خطا در ذخیره تغییرات');
      setMessageType('danger');
    } finally {
      setSaving(false);
    }
  };

  /* ============== Render ============== */
  if (loading) {
    return <FactorFormSkeleton />;
  }

  if (notFound) {
    return (
      <div className="card">
        <h2>فاکتور یافت نشد</h2>
        <p className="empty">
          فاکتوری با این شناسه وجود ندارد یا حذف شده است.
        </p>
        <div className="flexend">
          <Link className="btn secondary" to="/dashboard/factors">
            <i className="fa-solid fa-arrow-right" /> بازگشت به لیست
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      {message && (
        <div
          className={
            messageType === 'danger' ? 'toast-danger' : 'toast-success'
          }
        >
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit} id="factorForm">
        {/* ============ اطلاعات فاکتور ============ */}
        <div className="card">
          <h2>اطلاعات فاکتور</h2>
          <div className="grid">
            <div>
              <label>شماره سریال</label>
              <input
                value={serial}
                readOnly
                disabled
                style={{ opacity: 0.7, cursor: 'not-allowed' }}
              />
            </div>
            <div>
              <label>تاریخ فاکتور</label>
              <JalaliDatePicker
                value={date}
                onChange={(v) => {
                  setDate(v);
                  markDirty();
                }}
                placeholder="انتخاب تاریخ فاکتور"
                name="date"
                required
              />
            </div>
          </div>
        </div>

        {isDirty && (
          <div className="dirty-banner">
            <i className="fa-solid fa-triangle-exclamation" />
            تغییرات ذخیره نشده دارید. لطفاً قبل از خروج ذخیره کنید.
          </div>
        )}

        {/* ============ مشتری ============ */}
        <div className="card">
          <h2>مشخصات خریدار</h2>
          <CustomerPicker customer={customer} onSelect={handleSelectCustomer} />
        </div>

        {/* ============ اقلام ============ */}
        <div className="card">
          <h2>شرح فاکتور</h2>

          <FactorItemsTable
            items={items}
            setItems={setItems}
            onChange={markDirty}
          />

          <div className="grid" style={{ marginTop: 10 }}>
            <div>
              <label>شرایط پرداخت</label>
              <select
                value={paymentType}
                onChange={(e) => {
                  setPaymentType(e.target.value);
                  if (e.target.value !== 'پیش پرداخت') setPrepaid('');
                  markDirty();
                }}
              >
                <option>نقدی</option>
                <option>غیر نقدی</option>
                <option>پیش پرداخت</option>
              </select>
            </div>

            {paymentType === 'پیش پرداخت' && (
              <div>
                <label>مبلغ پیش پرداخت (ریال) *</label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  required
                  value={prepaid ? formatNumber(parseNum(prepaid)) : ''}
                  onChange={(e) => {
                    const raw = parseNum(e.target.value);
                    setPrepaid(raw ? String(raw) : '');
                    markDirty();
                  }}
                  placeholder="مثلاً 5,000,000"
                />
              </div>
            )}
            <div>
              <label>توضیحات</label>
              <input
                value={notes}
                onChange={(e) => {
                  setNotes(e.target.value);
                  markDirty();
                }}
              />
            </div>
          </div>
          {paymentType !== 'نقدی' && (
            <div className="muted" style={{ marginTop: 10, textAlign: 'left' }}>
              مبلغی که از حساب مشتری کسر می‌شود:{' '}
              <b>{formatNumber(Math.max(debitAmount, 0))} ریال</b>
            </div>
          )}
          <div className="flexend">
            <Link
              className="btn secondary"
              to="/dashboard/factors"
              style={{ marginLeft: 8 }}
            >
              <i className="fa-solid fa-xmark" /> انصراف
            </Link>
            <button className="btn" type="submit" disabled={saving}>
              {!saving && <i className="fa-solid fa-floppy-disk" />}
              <span className="btn-label">
                {saving ? 'در حال ذخیره...' : 'ذخیره تغییرات'}
              </span>
              {saving && (
                <span className="btn-spinner">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 50 50"
                    style={{ animation: 'spin 0.8s linear infinite' }}
                  >
                    <circle
                      cx="25"
                      cy="25"
                      r="20"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="5"
                      strokeLinecap="round"
                      strokeDasharray="90 150"
                    />
                  </svg>
                </span>
              )}
            </button>
          </div>
        </div>
      </form>
    </>
  );
};

export default Edit;
